import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import NotificationsPopover from '../notifications/NotificationsPopover';
import {
  Box, Drawer, Toolbar, List, ListItem, ListItemIcon, 
  ListItemText, Avatar, Typography, Divider, Chip,
  useTheme, useMediaQuery, IconButton, AppBar, Badge,
  Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, Button, ListItemButton, Paper, Alert,
  CircularProgress, alpha, Slide, Zoom, styled, Backdrop
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  Assessment as AssessmentIcon,
  Menu as MenuIcon,
  ExitToApp as LogoutIcon,
  Notifications as NotificationsIcon,
  Business as BusinessIcon,
  Security as SecurityIcon,
  AdminPanelSettings as AdminIcon,
  Receipt as ReceiptIcon,
  CreditCard as PaymentsIcon,
  SelfImprovement as MentalWellnessIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  Info as InfoIcon,
  AccountCircle as AccountCircleIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { format } from 'date-fns';

// Transition component for logout dialog
const SlideTransition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Styled component for the security icon
const StyledSecurityIcon = styled(SecurityIcon)(({ theme }) => ({
  fontSize: 48,
  color: alpha(theme.palette.secondary.main, 0.9),
  marginRight: theme.spacing(2),
}));

const SuperAdminSidebar = ({ children }) => {
  const { user, logout } = useAuth();
  const { tenant } = useTenant();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [logoutSuccess, setLogoutSuccess] = useState(false);
  const [loginTime, setLoginTime] = useState(null);
  
  // Set login time on component mount
  useEffect(() => {
    const storedLoginTime = localStorage.getItem('loginTime');
    if (storedLoginTime) {
      setLoginTime(new Date(parseInt(storedLoginTime)));
    } else {
      // If no login time is stored, set to now and store it
      const now = new Date();
      setLoginTime(now);
      localStorage.setItem('loginTime', now.getTime().toString());
    }
  }, []);
  
  // Safe function to get initials
  const getInitials = (firstName, lastName) => {
    const firstInitial = (firstName && typeof firstName === 'string' && firstName.length > 0) ? firstName.charAt(0) : '';
    const lastInitial = (lastName && typeof lastName === 'string' && lastName.length > 0) ? lastName.charAt(0) : '';
    return (firstInitial + lastInitial) || '?';
  };

  // Define sidebar menu items for SuperAdmin - updated to match App.jsx routes
  const menuItems = [
    { path: "/superadmin/dashboard", name: "Dashboard", icon: <DashboardIcon /> },
    { path: "/superadmin/tenants", name: "Tenants", icon: <BusinessIcon /> },
    { path: "/superadmin/users", name: "Users", icon: <PeopleIcon /> },
    { path: "/superadmin/subscription", name: "Management", icon: <ReceiptIcon /> },
    { path: "/superadmin/payments/plans", name: "Subscription Plans", icon: <PaymentsIcon /> },
    { path: "/superadmin/payments/settings", name: "Payment Settings", icon: <SettingsIcon /> },
    { path: "/superadmin/reset", name: "Mental Wellness", icon: <MentalWellnessIcon /> },
  ];

  // Handle logout confirmation dialog
  const openLogoutDialog = () => {
    setLogoutDialogOpen(true);
  };

  // Handle logout process
  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      await new Promise(resolve => setTimeout(resolve, 1000)); // Brief delay for visual feedback
      await logout();
      
      // Show success feedback briefly before redirecting
      setLogoutSuccess(true);
      localStorage.removeItem('loginTime');
      
      // Close the dialog and drawer after a short delay
      setTimeout(() => {
        setLogoutDialogOpen(false);
        setDrawerOpen(false);
        navigate('/login');
      }, 1500);
    } catch (error) {
      console.error('Logout failed:', error);
      // Even if there's an error, attempt to clear localStorage and redirect
      localStorage.clear();
      navigate('/login');
    }
  };

  const drawerWidth = 240;

  const drawer = (
    <Box>
      <Toolbar sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        py: 2 
      }}>
        <Avatar
          src={user?.profilePicture || ""}
          sx={{ 
            width: 80, 
            height: 80,
            mb: 1,
            bgcolor: theme.palette.secondary.main // Different color for SuperAdmin
          }}
          alt={`${user?.firstName || 'Super Admin'} ${user?.lastName || 'User'}`}
        >
          {getInitials(user?.firstName, user?.lastName)}
        </Avatar>
        <Typography variant="h6" sx={{ mt: 1 }}>
          {user?.firstName || 'Super Admin'} {user?.lastName || 'User'}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {user?.email || 'superadmin@example.com'}
        </Typography>
        <Chip
          label="Super Administrator"
          color="secondary"
          size="small"
          sx={{ mt: 1 }}
        />
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => {
          // Check if current path starts with this menu item's path
          const isActive = location.pathname === item.path || 
                          (item.path !== "/superadmin/dashboard" && location.pathname.startsWith(item.path));
          
          return (
            <ListItem 
              disablePadding
              key={item.path}
            >
              <ListItemButton
                component={Link} 
                to={item.path}
                selected={isActive}
                onClick={() => isMobile && setDrawerOpen(false)}  // Close drawer on mobile after click
                sx={{ 
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.08)' },
                  borderLeft: isActive ? '4px solid #ce93d8' : '4px solid transparent',
                  bgcolor: isActive ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                }}
              >
                <ListItemIcon sx={{ color: 'white' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.name}
                  primaryTypographyProps={{
                    sx: {
                      color: isActive ? '#fff' : 'rgba(255,255,255,0.8)',
                      fontWeight: isActive ? '500' : 'normal'
                    }
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
      <Divider />
      <List>
        <ListItem disablePadding>
          <ListItemButton 
            onClick={openLogoutDialog}
            sx={{ '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.08)' } }}
          >
            <ListItemIcon sx={{ color: 'white' }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', bgcolor: '#f5f7fa', minHeight: '100vh' }}>
      {/* App Bar for mobile - only show if needed */}
      <AppBar
        position="fixed"
        sx={{
          display: { md: 'none' },
          width: '100%',
          bgcolor: '#6a1b9a', // Purple color for SuperAdmin
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={() => setDrawerOpen(!drawerOpen)}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div">
            Super Admin Dashboard
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <NotificationsPopover />
        </Toolbar>
      </AppBar>
      
      {/* The drawer */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          ModalProps={{
            keepMounted: true, // Better mobile performance
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              bgcolor: '#38006b', // Darker purple background for SuperAdmin sidebar
              color: 'white',
              marginTop: { xs: '56px', sm: '64px' }, // Adjust for app bar height
            },
          }}
        >
          {drawer}
        </Drawer>
        
        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              bgcolor: '#38006b', // Darker purple background for SuperAdmin sidebar
              color: 'white'
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      
      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 0, // Remove all padding for desktop
          px: { xs: 0.5, md: 0 }, // Add horizontal padding only for mobile (xs and sm sizes)
          ml: 0, // Remove left margin
          width: { md: `calc(100% - ${drawerWidth}px)` },
          mt: { xs: 1, md: 0 },
        }}
      >
        <Toolbar sx={{ display: { md: 'none' }, minHeight: { xs: '24px' } }} />
        {children}
      </Box>

      {/* Enhanced Logout Confirmation Dialog */}
      <Dialog
        open={logoutDialogOpen}
        onClose={() => !isLoggingOut && setLogoutDialogOpen(false)}
        aria-labelledby="logout-dialog-title"
        aria-describedby="logout-dialog-description"
        TransitionComponent={SlideTransition}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: 24,
            width: { xs: '90%', sm: '450px' },
            overflow: 'hidden',
            backgroundImage: `radial-gradient(circle at top right, ${alpha(theme.palette.secondary.main, 0.08)}, transparent 70%)`,
          }
        }}
      >
        <DialogTitle 
          id="logout-dialog-title"
          sx={{
            display: 'flex',
            alignItems: 'center',
            pt: 3,
            pb: 2,
            background: `linear-gradient(to right, ${alpha(theme.palette.background.paper, 0.7)}, ${alpha(theme.palette.background.paper, 0.98)})`,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
          }}
        >
          {logoutSuccess ? (
            <Zoom in={logoutSuccess}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CheckCircleIcon color="success" sx={{ fontSize: 28, mr: 1.5 }} />
                <Typography variant="h6" component="div">
                  Logout Successful
                </Typography>
              </Box>
            </Zoom>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <StyledSecurityIcon />
              <Box>
                <Typography variant="h6" component="div">
                  Confirm Logout
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Super Admin Portal - {new Date().toLocaleDateString()}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogTitle>
        
        <DialogContent sx={{ p: 3, pt: 3 }}>
          {logoutSuccess ? (
            <Alert 
              severity="success" 
              variant="filled"
              sx={{ 
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                borderRadius: 1 
              }}
            >
              You have been successfully logged out. Redirecting to login page...
            </Alert>
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar
                  src={user?.profilePicture || ""}
                  sx={{ 
                    width: 50, 
                    height: 50,
                    border: `2px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
                    bgcolor: theme.palette.secondary.main,
                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                  }}
                >
                  {getInitials(user?.firstName, user?.lastName)}
                </Avatar>
                <Box sx={{ ml: 2 }}>
                  <Typography variant="subtitle1" fontWeight="medium">
                    {user?.firstName || 'Super Admin'} {user?.lastName || 'User'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {user?.email || 'superadmin@example.com'}
                  </Typography>
                </Box>
              </Box>
              
              <Paper 
                variant="outlined" 
                sx={{ 
                  p: 2, 
                  mb: 2.5, 
                  bgcolor: alpha(theme.palette.secondary.light, 0.04),
                  border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
                  borderRadius: 1.5
                }}
              >
                <Typography variant="body1" paragraph sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <InfoIcon color="secondary" sx={{ fontSize: 20, mr: 1, mt: 0.3, opacity: 0.8 }} />
                  Logging out will end your Super Admin session. You will need to sign in again to access your account.
                </Typography>
                
                {loginTime && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <AccessTimeIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 18 }} />
                    <Typography variant="body2" color="text.secondary">
                      Session started: {format(loginTime, 'MMM d, yyyy, h:mm a')}
                    </Typography>
                  </Box>
                )}
                
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <AdminIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 18 }} />
                  <Typography variant="body2" color="text.secondary">
                    Logged in as Super Administrator
                  </Typography>
                </Box>
              </Paper>
              
              <Alert 
                severity="warning" 
                icon={<WarningIcon />}
                variant="outlined"
                sx={{ 
                  borderRadius: 1.5,
                  fontSize: '0.875rem'
                }}
              >
                Any unsaved changes may be lost when you log out.
              </Alert>
            </>
          )}
        </DialogContent>
        
        {!logoutSuccess && (
          <DialogActions sx={{ px: 3, py: 2.5, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
            <Button 
              onClick={() => setLogoutDialogOpen(false)} 
              color="inherit"
              disabled={isLoggingOut}
              sx={{ 
                px: 3, 
                borderRadius: 1.5,
                '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.04) }
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleLogout} 
              color="secondary" 
              variant="contained" 
              disabled={isLoggingOut}
              sx={{ 
                px: 3,
                borderRadius: 1.5,
                boxShadow: 'none'
              }}
              startIcon={isLoggingOut ? <CircularProgress size={20} color="inherit" /> : <LogoutIcon />}
            >
              {isLoggingOut ? 'Logging out...' : 'Logout'}
            </Button>
          </DialogActions>
        )}
      </Dialog>
      
      {/* Backdrop for logout success */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={logoutSuccess}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

export default SuperAdminSidebar;